/**
 * §PaymentPlan calculator — client-side mirror of the backend `PaymentPlanFormulaService` (+ its safe expression
 * evaluator), so the customer-facing payment plan renders instantly from the configured EQUATIONS, exactly as
 * `lib/finance.ts` mirrors the DBR math. The backend stays the authority when the plan is sent to the customer
 * (it recomputes the same config server-side). Defaults reproduce `Calculator.xlsx` (quarterly amortization).
 */

export type PaymentFrequency = 'Monthly' | 'Quarterly' | 'Special';

export const MONTHS_PER_QUARTER = 3;

/** §Special-plan: leading monthly instalments when the case answers nothing — the original shipped shape. */
export const DEFAULT_LEADING_MONTHLY_PERIODS = MONTHS_PER_QUARTER;

/**
 * §Special-leading: the counts an agent may pick, in months — every one a WHOLE number of quarters.
 *
 * Because `3k × (Q/3) = k × Q`, splitting `k` quarters leaves the pricing untouched: same PMT, same totals,
 * same flat rate. A count that is not a multiple of three is a different annuity and is not offered.
 */
export const LEADING_MONTHLY_OPTIONS = [3, 6, 9, 12];

/**
 * §Special-leading: the leading monthly count this plan actually runs on — 0 for Monthly and Quarterly, so
 * `leading > 0` always means "this is a Special plan". Snapped to a whole quarter, to at least one, and to at
 * most `numPeriods - 1` so a quarterly leg survives. Mirrors `resolveLeadingMonthlyPeriods` on the backend.
 */
export function resolveLeadingMonthlyPeriods(raw: unknown, frequency: PaymentFrequency, numPeriods: number): number {
	if (frequency !== 'Special') return 0;
	const periods = Math.max(0, Math.round(numPeriods));
	if (periods <= 1) return 0;
	const parsed = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
	const months = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LEADING_MONTHLY_PERIODS;
	return Math.max(1, Math.min(Math.round(months / MONTHS_PER_QUARTER), periods - 1)) * MONTHS_PER_QUARTER;
}



export interface PaymentPlanScheduleFormulas {
	interest: string;
	principal: string;
}

export interface PaymentPlanSendConfig {
	enabled: boolean;
	notifyTemplate?: string;
	recipient?: string;
}

export interface PaymentPlanConfig {
	constants: Record<string, number>;
	formulas: Record<string, string>;
	schedule: PaymentPlanScheduleFormulas;
	/** Which amortization-schedule data columns to display (keys of PAYMENT_PLAN_SCHEDULE_COLUMNS); '#' is always shown. */
	scheduleColumns: string[];
	/**
	 * §PaymentPlan-outputs: which HEADLINE values to display (keys of PAYMENT_PLAN_OUTPUT_META).
	 * Served per placement by workflow-manager, the symmetric twin of `scheduleColumns`. Absent or
	 * empty ⇒ show them all, so a placement that never configured it is unchanged. Display only —
	 * a hidden output is still computed, and anything derived from it is unaffected.
	 */
	outputColumns?: string[];
	sendToCustomer: PaymentPlanSendConfig;
}

/** The toggleable amortization-schedule data columns (the period '#' column is always rendered first). */
export const PAYMENT_PLAN_SCHEDULE_COLUMNS: {key: Exclude<keyof PaymentPlanScheduleRow, 'dueDate'>; label: string}[] = [
	{key: 'balance', label: 'Opening Balance'},
	{key: 'interest', label: 'Interest'},
	{key: 'principal', label: 'Principal'},
	{key: 'installment', label: 'Installment'},
	{key: 'outstanding', label: 'Outstanding'},
];

export interface PaymentPlanInputs {
	cashPrice?: number;
	downPayment?: number;
	tenorYears?: number;
	frequency?: string;
	leadingMonths?: unknown;
}

export interface PaymentPlanScheduleRow {
	period: number;
	dueDate?: string;
	monthOffset: number;
	balance: number;
	interest: number;
	principal: number;
	installment: number;
	outstanding: number;
}

export interface PaymentPlanResult {
	summary: Record<string, number>;
	schedule: PaymentPlanScheduleRow[];
	periodsPerYear: number;
	frequency: PaymentFrequency;
	leadingMonths?: number;
}

export type OutputFormat = 'money' | 'percent' | 'number';

/** Display order + formatting for the headline summary. Keys not listed are still computed but hidden. */
/**
 * Every headline value the calculator can display, with its label and formatting. This is the
 * CATALOGUE, not the default view — a placement's `outputColumns` may name any of these.
 */
export const PAYMENT_PLAN_OUTPUT_META: {key: string; label: string; format: OutputFormat}[] = [
	{key: 'loanAmount', label: 'Loan Amount', format: 'money'},
	{key: 'ltv', label: 'LTV', format: 'percent'},
	{key: 'installment', label: 'Installment', format: 'money'},
	{key: 'numPeriods', label: 'Installments', format: 'number'},
	{key: 'totalInstallments', label: 'Total Installments', format: 'money'},
	{key: 'interestRevenue', label: 'Interest Revenue', format: 'money'},
	{key: 'flatRate', label: 'Flat Rate', format: 'percent'},
	{key: 'requiredIncome', label: 'Required Net Income', format: 'money'},
];

/**
 * §PaymentPlan-outputs: what the grid shows when the placement configures nothing — the four
 * figures workflow-manager itself shows.
 *
 * WM trimmed its own headline grid to exactly these on 2026-07-27 ("the four figures the credit
 * reviewer acts on"), and its list is a hardcoded constant — the WM step screen never reads
 * `outputColumns` at all. The CRM defaulted the other way, to "show everything", so an
 * unconfigured placement rendered eight cards here against WM's four, surfacing Interest Revenue,
 * Flat Rate, Total Installments and Required Net Income on a screen that is meant to mirror WM.
 *
 * The other four are NOT deleted: they stay computed, stay in the result summary, still print on
 * the customer payment-plan PDF, and a placement that explicitly names them in `outputColumns`
 * still gets them. Only the unconfigured default changes.
 */
export const PAYMENT_PLAN_DEFAULT_OUTPUTS: string[] = [
	'loanAmount',
	'ltv',
	'installment',
	'numPeriods',
];

export const DEFAULT_PAYMENT_PLAN_CONFIG: PaymentPlanConfig = {
	constants: {
		annualRatePct: 32,
		monthlyPeriods: 12,
		quarterlyPeriods: 4,
		requiredIncomeDbrPct: 40,
		landCostPct: 0,
		insuranceRatePct: 0,
	},
	formulas: {
		loanAmount: 'cashPrice - downPayment',
		ltv: 'loanAmount / cashPrice',
		periodicRate: 'annualRatePct / 100 / periodsPerYear',
		numPeriods: 'tenorYears * periodsPerYear',
		installment: 'PMT(periodicRate, numPeriods, -loanAmount)',
		totalInstallments: 'installment * numPeriods',
		interestRevenue: 'totalInstallments - loanAmount',
		flatRate: 'interestRevenue / loanAmount / tenorYears',
		requiredIncome: 'installment / (requiredIncomeDbrPct / 100)',
	},
	schedule: {interest: 'balance * periodicRate', principal: 'installment - interest'},
	scheduleColumns: ['balance', 'interest', 'principal', 'installment', 'outstanding'],
	sendToCustomer: {enabled: false},
};

// ── safe expression evaluator (no eval) ───────────────────────────────────────────────────────────────────────

type Evaluator = (scope: Record<string, number>) => number;
interface CompiledExpression {
	evaluate: Evaluator;
	variables: string[];
}
type TokenType = 'num' | 'id' | 'op' | 'lparen' | 'rparen' | 'comma';
interface Token {
	type: TokenType;
	value: string;
}

const FUNCTIONS: Record<string, (args: number[]) => number> = {
	PMT: ([rate, nper, pv]: number[]): number => {
		if (nper <= 0) return 0;
		if (rate === 0) return -pv / nper;
		return -(pv * rate) / (1 - Math.pow(1 + rate, -nper));
	},
	ROUND: ([x, dp = 0]: number[]): number => {
		const factor = Math.pow(10, dp);
		return Math.round(x * factor) / factor;
	},
	MIN: (args: number[]): number => Math.min(...args),
	MAX: (args: number[]): number => Math.max(...args),
	POW: ([base, exp]: number[]): number => Math.pow(base, exp),
	ABS: ([x]: number[]): number => Math.abs(x),
	SUM: (args: number[]): number => args.reduce((sum, value) => sum + value, 0),
};

function tokenize(input: string): Token[] {
	const tokens: Token[] = [];
	const isDigit = (c: string): boolean => c >= '0' && c <= '9';
	let i = 0;
	while (i < input.length) {
		const c = input[i];
		if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
			i++;
			continue;
		}
		if (isDigit(c) || (c === '.' && isDigit(input[i + 1] ?? ''))) {
			let j = i + 1;
			while (j < input.length && (isDigit(input[j]) || input[j] === '.')) j++;
			tokens.push({type: 'num', value: input.slice(i, j)});
			i = j;
			continue;
		}
		if (/[A-Za-z_]/.test(c)) {
			let j = i + 1;
			while (j < input.length && /[A-Za-z0-9_]/.test(input[j])) j++;
			tokens.push({type: 'id', value: input.slice(i, j)});
			i = j;
			continue;
		}
		if (c === '(') tokens.push({type: 'lparen', value: c});
		else if (c === ')') tokens.push({type: 'rparen', value: c});
		else if (c === ',') tokens.push({type: 'comma', value: c});
		else if ('+-*/^'.includes(c)) tokens.push({type: 'op', value: c});
		else throw new Error(`Unexpected character '${c}'`);
		i++;
	}
	return tokens;
}

class ExpressionParser {
	private pos = 0;
	private readonly vars = new Set<string>();
	private readonly tokens: Token[];

	constructor(tokens: Token[]) {
		this.tokens = tokens;
	}

	parse(): CompiledExpression {
		if (this.tokens.length === 0) throw new Error('Empty expression');
		const evaluate = this.parseExpression();
		if (this.pos !== this.tokens.length) throw new Error('Unexpected trailing tokens');
		return {evaluate, variables: [...this.vars]};
	}

	private peek(): Token | undefined {
		return this.tokens[this.pos];
	}
	private next(): Token | undefined {
		return this.tokens[this.pos++];
	}
	private expect(type: TokenType): void {
		const token = this.next();
		if (!token || token.type !== type) throw new Error(`Expected ${type}`);
	}

	private parseExpression(): Evaluator {
		let left = this.parseTerm();
		for (;;) {
			const token = this.peek();
			if (token?.type !== 'op' || (token.value !== '+' && token.value !== '-')) break;
			this.next();
			const right = this.parseTerm();
			const prev = left;
			left = token.value === '+' ? (s): number => prev(s) + right(s) : (s): number => prev(s) - right(s);
		}
		return left;
	}

	private parseTerm(): Evaluator {
		let left = this.parsePower();
		for (;;) {
			const token = this.peek();
			if (token?.type !== 'op' || (token.value !== '*' && token.value !== '/')) break;
			this.next();
			const right = this.parsePower();
			const prev = left;
			left = token.value === '*' ? (s): number => prev(s) * right(s) : (s): number => prev(s) / right(s);
		}
		return left;
	}

	private parsePower(): Evaluator {
		const base = this.parseUnary();
		const token = this.peek();
		if (token?.type === 'op' && token.value === '^') {
			this.next();
			const exp = this.parsePower();
			return (s): number => Math.pow(base(s), exp(s));
		}
		return base;
	}

	private parseUnary(): Evaluator {
		const token = this.peek();
		if (token?.type === 'op' && (token.value === '+' || token.value === '-')) {
			this.next();
			const operand = this.parseUnary();
			return token.value === '-' ? (s): number => -operand(s) : operand;
		}
		return this.parsePrimary();
	}

	private parsePrimary(): Evaluator {
		const token = this.next();
		if (!token) throw new Error('Unexpected end of expression');
		if (token.type === 'num') {
			const value = Number(token.value);
			if (Number.isNaN(value)) throw new Error(`Invalid number '${token.value}'`);
			return (): number => value;
		}
		if (token.type === 'lparen') {
			const inner = this.parseExpression();
			this.expect('rparen');
			return inner;
		}
		if (token.type === 'id') {
			if (this.peek()?.type === 'lparen') {
				this.next();
				const args: Evaluator[] = [];
				if (this.peek()?.type !== 'rparen') {
					args.push(this.parseExpression());
					while (this.peek()?.type === 'comma') {
						this.next();
						args.push(this.parseExpression());
					}
				}
				this.expect('rparen');
				const fn = FUNCTIONS[token.value.toUpperCase()];
				if (!fn) throw new Error(`Unknown function '${token.value}'`);
				return (s): number => fn(args.map((arg) => arg(s)));
			}
			const name = token.value;
			this.vars.add(name);
			return (s): number => {
				const value = s[name];
				if (value === undefined) throw new Error(`Unknown variable '${name}'`);
				if (typeof value !== 'number' || Number.isNaN(value)) throw new Error(`Variable '${name}' is not a number`);
				return value;
			};
		}
		throw new Error(`Unexpected token '${token.value}'`);
	}
}

/** Compile an arithmetic expression string into a reusable `(scope) => number` evaluator. Throws on a syntax error. */
export function compileExpression(expression: string): CompiledExpression {
	return new ExpressionParser(tokenize(expression)).parse();
}

// ── payment-plan engine (mirrors PaymentPlanFormulaService) ────────────────────────────────────────────────────

const SCHEDULE_VARS = new Set(['balance', 'interest', 'principal']);
const MAX_SCHEDULE_PERIODS = 1200;

export function normalizeFrequency(raw: unknown): PaymentFrequency {
 const value = String(raw ?? '').trim().toLowerCase();
 if (value.startsWith('spec')) return 'Special';
 return value.startsWith('quart') ? 'Quarterly' : 'Monthly';
}

/** True when the inputs needed to CALCULATE a plan (cash price + tenor) are present. */
export function hasRequiredInputs(inputs: PaymentPlanInputs): boolean {
	return Boolean(inputs.cashPrice && inputs.cashPrice > 0 && inputs.tenorYears && inputs.tenorYears > 0);
}

/**
 * True when the inputs needed to JUDGE a plan are present. Deliberately weaker than
 * `hasRequiredInputs`: the down-payment and loan-amount rules are decidable from cash price + down
 * payment alone, and waiting for a tenor left the agent able to save a plan the rules already reject.
 */
export function hasJudgeableInputs(inputs: PaymentPlanInputs): boolean {
	return Boolean(inputs.cashPrice && inputs.cashPrice > 0 && inputs.downPayment !== undefined && inputs.downPayment !== null);
}

function resolveFormulas(formulas: Record<string, string>, base: Record<string, number>): Record<string, number> {
	const compiled = Object.entries(formulas).map(([key, expression]) => ({key, ...compileExpression(expression)}));
	const scope: Record<string, number> = {...base};
	const known = new Set(Object.keys(scope));
	const out: Record<string, number> = {};
	const pending = [...compiled];
	let progressed = true;
	while (pending.length > 0 && progressed) {
		progressed = false;
		for (let idx = pending.length - 1; idx >= 0; idx--) {
			const formula = pending[idx];
			const deps = formula.variables.filter((name) => !SCHEDULE_VARS.has(name));
			if (!deps.every((name) => known.has(name))) continue;
			const value = formula.evaluate(scope);
			scope[formula.key] = value;
			out[formula.key] = value;
			known.add(formula.key);
			pending.splice(idx, 1);
			progressed = true;
		}
	}
	if (pending.length > 0) {
		throw new Error(`Could not resolve formulas: ${pending.map((f) => f.key).join(', ')}`);
	}
	return out;
}

function buildSchedule(
	config: PaymentPlanConfig,
	scope: Record<string, number>,
	periodsPerYear: number,
	leading: number,
): PaymentPlanScheduleRow[] {
	const interestExpr = compileExpression(config.schedule.interest);
	const principalExpr = compileExpression(config.schedule.principal);
	const pricingPeriods = Math.max(0, Math.round(scope.numPeriods ?? 0));
	// Splitting a quarter turns one row into three, so each split quarter nets +2 rows.
	const leadingQuarters = Math.max(0, Math.round(leading / MONTHS_PER_QUARTER));
	const periods = Math.min(pricingPeriods === 0 ? 0 : pricingPeriods + leading - leadingQuarters, MAX_SCHEDULE_PERIODS);
	const monthsPerPeriod = Math.max(1, Math.round(12 / (periodsPerYear || 12)));
	const installment = scope.installment ?? 0;
	const schedule: PaymentPlanScheduleRow[] = [];
	let balance = scope.loanAmount ?? 0;
	for (let period = 1; period <= periods; period++) {
		const isLeading = period <= leading;
		const rowInstallment = isLeading ? installment / MONTHS_PER_QUARTER : installment;
		const rowScope = isLeading
			? {...scope, periodicRate: (scope.periodicRate ?? 0) / MONTHS_PER_QUARTER, installment: rowInstallment}
			: scope;
		const interest = interestExpr.evaluate({...rowScope, balance});
		const row = closeRow(
			{
				period,
				balance,
				interest,
				principal: 0,
				installment: rowInstallment,
				outstanding: 0,
				monthOffset: isLeading ? period : monthsPerPeriod * (period - leading + leadingQuarters),
			},
			principalExpr.evaluate({...rowScope, balance, interest}),
			period === periods,
			leading,
		);
		schedule.push(row);
		balance = row.outstanding;
	}
	return schedule;
}

/**
 * Settle ONE row's principal and closing balance.
 *
 * §Special-plan: paying the leading quarters early accrues less interest than the annuity assumed, so the
 * last row of a Special schedule no longer closes on its own. The business sheet keeps the final instalment
 * whole and books the difference as interest; the principal column clears whatever balance remains. A plain
 * Monthly/Quarterly last row just has its floating residual clamped to a clean zero.
 */
function closeRow(row: PaymentPlanScheduleRow, principal: number, isLast: boolean, leading: number): PaymentPlanScheduleRow {
	if (isLast && leading > 0) {
		return {...row, principal: row.balance, interest: row.installment - row.balance, outstanding: 0};
	}
	const outstanding = row.balance - principal;
	if (isLast && Math.abs(outstanding) < 1) {
		return {...row, principal, outstanding: 0};
	}
	return {...row, principal, outstanding};
}


/** Evaluate the configurable payment plan (headline summary + amortization schedule). Throws on a bad formula. */
export function evaluatePaymentPlan(config: PaymentPlanConfig, inputs: PaymentPlanInputs): PaymentPlanResult {
	if (!Number.isFinite(config.constants.annualRatePct) || config.constants.annualRatePct < 0) {
		throw new Error('Annual interest rate must be a finite nonnegative number.');
	}
	const frequency = normalizeFrequency(inputs.frequency);
	const periodsPerYear =
		frequency === 'Monthly' ? Number(config.constants.monthlyPeriods ?? 12) : Number(config.constants.quarterlyPeriods ?? 4);

	const baseScope: Record<string, number> = {
		...config.constants,
		cashPrice: inputs.cashPrice ?? 0,
		downPayment: inputs.downPayment ?? 0,
		tenorYears: inputs.tenorYears ?? 0,
		periodsPerYear,
	};

	const summary = resolveFormulas(config.formulas, baseScope);
	const scope = {...baseScope, ...summary};

	const leading = resolveLeadingMonthlyPeriods(inputs.leadingMonths, frequency, scope.numPeriods ?? 0);
	const schedule = buildSchedule(config, scope, periodsPerYear, leading);
	summary.paymentCount = schedule.length;
	if (Object.values(summary).some(value => !Number.isFinite(value)) ||
		schedule.some(row => Object.values(row).some(value => typeof value === 'number' && !Number.isFinite(value)))) {
		throw new Error('Payment plan calculation must produce finite values.');
	}
	return {summary, schedule, periodsPerYear, frequency, leadingMonths: leading};
}
